import type { BookedAppointment, ScreeningProgram } from "../hooks"
import { useBookingOffering } from "../hooks"
import { appointmentTypeCopy } from "../preferencesCopy"
import { Box, Button, SuccessIcon, Typography, dayjs } from "@carrotfertility/carotene-core"
import React from "react"

// The care setting shown on the "Visit" line — a stand-in until the offering carries a display
// name for the clinic/program.
const CLINIC_LABEL = "Carrot Clinic"

type BookedPageProps = {
  program: ScreeningProgram
  // The appointment returned by the booking call — the source of the assigned provider and the
  // visit window. Null only if the screen is reached without a recorded booking (defensive).
  appointment: BookedAppointment | null
  // The visit type the member booked; keys the offering lookup for the "Visit" line.
  appointmentTypeId?: string
  // The logged-in member's name, for the "For" line. Care-recipient-as-partner is deferred
  // (MEM-719), so this is always the member for now.
  memberName?: string
  onBackToHome?: () => void
}

// The booking confirmation ("You're booked") screen. Reached once the booking call succeeds; it
// summarizes the appointment and sends the member home. The confirmation email + calendar invite
// are server-side, and the join link is fetched just-in-time nearer the visit — neither is shown
// here (see MEM-719).
export function BookedPage({
  program,
  appointment,
  appointmentTypeId,
  memberName,
  onBackToHome
}: BookedPageProps): JSX.Element {
  // Offering is already cached from the preferences screen; this resolves the booked visit type
  // to its display title + duration for the "Visit" line.
  const offeringQuery = useBookingOffering(program)
  const appointmentType = offeringQuery.data?.appointmentTypes.find((type) => type.id === appointmentTypeId)

  const detailRows = [
    memberName ? { label: "For", value: memberName } : null,
    appointmentType
      ? {
          label: "Visit",
          value: `${CLINIC_LABEL} · ${appointmentTypeCopy(appointmentType.kind).title} (${
            appointmentType.durationMinutes
          } mins)`
        }
      : null,
    appointment
      ? {
          label: "Provider",
          value: appointment.provider.credential
            ? `${appointment.provider.name}, ${appointment.provider.credential}`
            : appointment.provider.name
        }
      : null,
    appointment ? { label: "Date & time", value: dayjs(appointment.startTime).format("MMM D · h:mm A") } : null
  ].filter((row): row is { label: string; value: string } => row !== null)

  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(theme.tokens.spacing.md),
        maxInlineSize: 640,
        marginInline: "auto",
        textAlign: "center"
      })}
    >
      <Box
        sx={(theme) => ({
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          inlineSize: 64,
          blockSize: 64,
          borderRadius: "50%",
          backgroundColor: theme.palette.success.light
        })}
      >
        <SuccessIcon fontSize="large" sx={(theme) => ({ color: theme.palette.success.main })} />
      </Box>

      <Box sx={(theme) => ({ display: "flex", flexDirection: "column", gap: theme.spacing(theme.tokens.spacing.xs) })}>
        <Typography variant="h1">You&apos;re booked</Typography>
        <Typography color="text.secondary">
          A confirmation and calendar invite are on their way to your email.
        </Typography>
      </Box>

      {detailRows.length > 0 && (
        <Box
          sx={(theme) => ({
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(theme.tokens.spacing.sm),
            inlineSize: "100%",
            maxInlineSize: 480,
            padding: theme.spacing(theme.tokens.spacing.lg),
            textAlign: "start",
            backgroundColor: theme.palette.background.paper,
            borderRadius: theme.tokens.borderRadius.md
          })}
        >
          <Typography variant="h5" component="h2">
            Appointment details
          </Typography>
          <Box
            component="dl"
            sx={(theme) => ({
              display: "flex",
              flexDirection: "column",
              gap: theme.spacing(theme.tokens.spacing.xs),
              margin: 0
            })}
          >
            {detailRows.map((row) => (
              <Box
                key={row.label}
                sx={(theme) => ({
                  display: "flex",
                  justifyContent: "space-between",
                  gap: theme.spacing(theme.tokens.spacing.md)
                })}
              >
                <Typography component="dt" color="text.secondary">
                  {row.label}
                </Typography>
                <Typography component="dd" sx={{ margin: 0, textAlign: "end" }}>
                  {row.value}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <Box>
        <Button onClick={onBackToHome} color="secondary">
          Back to home
        </Button>
      </Box>
    </Box>
  )
}
